import imgPlaceholder from "../assets/images/icon-placeholder.svg";
import axios from "axios";
import {getConfigData} from "../middleware";

const countPristine = (photos) => {
  let photosLen = 0;
  photos.forEach((current) => {
    if (false === current.pristine) {
      photosLen++;
    }
  });
  return photosLen;
}

// Enhanced loadImage function with progress tracking and proper async handling
const loadImage = (files, photos, setPhotos, photosKey, cdnUri, setUploadProgress, setIsUploading) => {
  return new Promise((resolve, reject) => {
    if (files.length === 0) {
      if (setIsUploading) setIsUploading(false);
      resolve([]);
      return;
    }
    
    console.log("loadImages - pre:", files);
    
    // Create promises for each file upload with progress tracking
    const uploadPromises = files.map((file, fileIndex) => {
      return new Promise(async (resolveFile, rejectFile) => {
        try {
          // Upload the file and track progress
          const result = await uploadFileWithProgress(
            file, 
            photosKey, 
            (progress) => {
              if (setUploadProgress) {
                // Update progress for this specific file
                setUploadProgress(prev => ({
                  ...prev,
                  [fileIndex]: Math.round(progress * 100)
                }));
              }
            }
          );
          
          resolveFile({src: result, size: file.size});
        } catch (error) {
          rejectFile(error);
        }
      });
    });
    
    Promise.allSettled(uploadPromises)
      .then((results) => {
        // Process results and update photos
        let _photos = [...photos]; // Create new array to trigger re-render
        
        let successCount = 0;
        for (let i = 0; i < _photos.length && successCount < results.length; i++) {
          if (true === _photos[i].pristine) {
            const result = results[successCount];
            
            if (result.status === 'fulfilled') {
              // Successful upload
              _photos[i] = {
                src: cdnUri + result.value.src, 
                pristine: false, 
                size: result.value.size
              };
            } else {
              // Failed upload - show error to user
              console.error("Failed to upload file:", result.reason);
            }
            
            successCount++;
          }
        }
        
        // Clear progress tracking
        if (setUploadProgress) {
          setUploadProgress({});
        }
        
        // Update state with new photos
        setPhotos(_photos);
        
        // Hide loading indicator
        if (setIsUploading) {
          setIsUploading(false);
        }
        
        // Resolve the main promise
        resolve(_photos);
      })
      .catch(error => {
        console.error("loadImages_error::", error);
        if (setIsUploading) {
          setIsUploading(false);
        }
        reject(error);
      });
  });
};

// Enhanced upload function with progress tracking
const uploadFileWithProgress = (file, photosKey, onProgress) => {
  return new Promise((resolve, reject) => {
    const config = getConfigData()[getConfigData().env].tempUploads;
    const url = config.schema + "://" + config.host + "/" + config.context + "/" + config.path;
    let uri = photosKey + "/" + file.name;
    
    // First, get the presigned URL
    axios(url + "?fileName=" + file.name + "&key=" + photosKey)
      .then(response => {
        if (response.data.success) {
          const uploadUrl = response.data.fileUploadURL;
          
          // Create XMLHttpRequest to track upload progress
          const xhr = new XMLHttpRequest();
          
          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              const progress = event.loaded / event.total;
              onProgress(progress);
            }
          });
          
          xhr.addEventListener("load", () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              console.log("Upload successful:", xhr.response);
              resolve("uploads/" + uri);
            } else {
              console.error("Upload failed:", xhr.statusText);
              reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
            }
          });
          
          xhr.addEventListener("error", () => {
            console.error("Upload error:", xhr.statusText);
            reject(new Error(`Upload error: ${xhr.statusText}`));
          });
          
          xhr.open("PUT", uploadUrl);
          xhr.setRequestHeader("Content-Type", file.type);
          xhr.send(file);
        } else {
          console.error(response.data.error);
          reject(new Error(response.data.error));
        }
      })
      .catch(error => {
        console.error("Error getting presigned URL:", error);
        reject(error);
      });
  });
};

// Original uploadFile function (kept for backward compatibility)
const uploadFile = (file, photosKey) => {
  const config = getConfigData()[getConfigData().env].tempUploads;
  const url = config.schema + "://" + config.host + "/" + config.context + "/" + config.path;
  let uri = photosKey+"/"+file.name;
  // Getting the signed url
  axios(
    url + "?fileName=" + file.name + "&key=" + photosKey
  ).then(response => {
    if (response.data.success) {

      // Getting the url from response
      const url = response.data.fileUploadURL;
      const body = new FormData();
      body.append("file", file);

      axios({
        method: "PUT",
        url: url,
        data: file,
        headers: {"Content-Type": file.type},
      })
        .then(res => {
          console.log(res);
        })
        .catch(err => {
          console.log("error:Error Occured while uploading the file");
          return Promise.reject(err);
        });
    } else {
      console.error(response.data.error);
      return Promise.reject(response.data.error);
    }
  });
  return "uploads/" + uri;
}

const initPhotos = (photos = []) => {
  const photoArr = [...photos]; // Create new array to avoid mutation
  if (photos.length < 4) { // Changed from <= to <
    let pad = 4 - photos.length;
    for (let i = 0; i < pad; i++) {
      photoArr.push({src: imgPlaceholder, pristine: true, size: 0});
    }
  }
  let photosLen = countPristine(photoArr);
  return {
    photos: photoArr,
    isCameraDisabled: (photosLen < 1)
  };
}

const getPhotosSize = (photos) => {
  let result = 0;
  for (const photo of photos) {
    if (false === photo.pristine) {
      result += photo.size;
    }
  }
  if (isNaN(result)) {
    result = 0;
  }
  return result;
}

export {initPhotos, countPristine, loadImage, getPhotosSize, uploadFile};